<template>
  <div class="norem-page">
    <!-- 导航栏 -->
    <nav class="nav" :class="{ 'nav-scrolled': isScrolled }">
      <div class="nav-inner">
        <div class="nav-logo">
          <img class="logo-icon" src="@img/novelix-website/logo.png" alt="Novelix" />
          <span class="logo-text">Novelix</span>
        </div>
        <div class="nav-links">
          <span class="nav-link" :class="{ active: currentPage === 'home' }" @click="goHome">Home</span>
          <span class="nav-link" :class="{ active: currentPage === 'write' }" @click="goWrite">Write</span>
        </div>
      </div>
    </nav>

    <!-- 首页：整张图片 -->
    <div v-if="currentPage === 'home'" class="home-page">
      <img class="home-img" src="@img/novelix-website/home.webp" alt="home" />
    </div>

    <!-- Write 表单页 -->
    <div v-else-if="currentPage === 'write'" class="write-page">
      <!-- Banner Hero 区 -->
      <div class="hero">
        <img class="hero-img" src="@img/novelix-website/banner.webp" alt="banner" />
      </div>

      <!-- 表单区 -->
      <div class="form-section">
        <h2 class="form-title">Novel Information</h2>
        <form class="form" @submit.prevent="handleSubmit">
          <div class="form-item">
            <label class="form-label"><span class="required">*</span> Book Title</label>
            <div class="form-input-wrap">
              <input
                v-model="form.bookTitle"
                class="form-input"
                :class="{ error: errors.bookTitle }"
                type="text"
                placeholder="Within in 30 Characters"
                maxlength="30"
              />
              <p v-if="errors.bookTitle" class="error-tip">Please try again</p>
            </div>
          </div>

          <div class="form-item">
            <label class="form-label"><span class="required">*</span>Author</label>
            <div class="form-input-wrap">
              <input
                v-model="form.editor"
                class="form-input"
                :class="{ error: errors.editor }"
                type="text"
                placeholder="Within in 30 Characters"
                maxlength="30"
              />
              <p v-if="errors.editor" class="error-tip">Please try again</p>
            </div>
          </div>

          <div class="form-item">
            <label class="form-label"><span class="required">*</span> Contract Type</label>
            <div class="form-radio-group">
              <label class="radio-label" v-for="type in contractTypes" :key="type.value">
                <input type="radio" v-model="form.contractType" :value="type.value" />
                <span class="radio-custom" :class="{ checked: form.contractType === type.value }"></span>
                <span class="radio-text">{{ type.label }}</span>
              </label>
            </div>
          </div>

          <div class="form-item">
            <label class="form-label"><span class="required">*</span> Email</label>
            <div class="form-input-wrap">
              <input
                v-model="form.email"
                class="form-input"
                :class="{ error: errors.email }"
                type="text"
                placeholder="Please enter your email"
                maxlength="30"
              />
              <p v-if="errors.email" class="error-tip">{{ errors.email }}</p>
            </div>
          </div>

          <div class="form-item form-item-upload">
            <label class="form-label"><span class="required">*</span> Publish your Novel</label>
            <div class="upload-area">
              <div v-if="uploadedFiles.length" class="file-list">
                <div class="file-item" v-for="(file, index) in uploadedFiles" :key="index">
                  <div class="file-icon" :class="file.ext">
                    <span class="file-ext">{{ file.ext.toUpperCase() }}</span>
                  </div>
                  <span class="file-name">{{ file.name }}</span>
                </div>
              </div>
              <label class="upload-btn" :class="{ error: errors.file || errors.fileSize || errors.fileType }">
                <input ref="fileInput" type="file" accept=".docx,.txt" @change="handleFileChange" style="display:none" />
                <img class="upload-icon" :src="(errors.file || errors.fileSize || errors.fileType) ? uploadWarningImg : uploadImg" alt="upload" />
                <span :class="{ 'upload-text-error': errors.file }">Update</span>
              </label>
              <p class="upload-tip" :class="{ 'error-tip': errors.fileType }">
                Only docx and txt formats are supported.
              </p>
              <p v-if="errors.fileSize" class="error-tip">File size cannot exceed 10MB</p>
            </div>
          </div>

          <div class="form-item form-item-agreement">
            <label class="form-label"></label>
            <div class="form-input-wrap">
              <label class="agreement-label">
                <input type="checkbox" v-model="agreed" class="agreement-checkbox" />
                <span class="checkbox-custom" :class="{ checked: agreed }"></span>
                <span class="agreement-text">
                  I have read and agree to the
                  <a class="agreement-link" :href="termsUrl" target="_blank">upload terms and conditions</a>
                </span>
              </label>
              <p v-if="errors.agreement" class="error-tip">Please try again</p>
            </div>
          </div>

          <div class="form-submit">
            <button class="submit-btn" type="submit" :disabled="submitting">
              <span v-if="submitting" class="btn-loading"></span>
              {{ submitting ? 'Submitting...' : 'Create' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- 提交成功页 -->
    <div v-else-if="currentPage === 'success'" class="success-page">
      <div class="success-content">
        <img class="success-icon" src="@img/novelix-website/check_circled.png" alt="success" />
        <p class="success-text">
          <strong>Thank you for your submission</strong><br />
          <strong>We will contact you as soon as possible.</strong>
        </p>
        <button class="submit-btn" @click="createNext">Create the next</button>
      </div>
    </div>

    <!-- Footer -->
    <footer class="footer">
      <p class="footer-title">Contact</p>
      <div class="footer-links">
        <span>CloudSpark Interactive Limited</span>
        <span class="footer-divider">|</span>
        <a href="mailto:service@novelix.live">service@novelix.live</a>
        <span class="footer-divider">|</span>
        <a href="../novelix-copyright/index.html" target="_blank">DMCA abuse</a>
      </div>
      <p class="footer-copy">© Copyright 2026 Novelix All Rights reserved</p>
    </footer>
  </div>
</template>

<script setup>
import { ref, reactive, watch, onMounted, onUnmounted, nextTick } from 'vue'
import uploadImg from '@img/novelix-website/upload.png'
import uploadWarningImg from '@img/novelix-website/upload_warning.png'
import { uploadNovel, createSubmission } from './api'

const currentPage = ref('home')

// 协议链接
const termsUrl = '/static/terms-of-service-en.html'

// 导航栏滚动透明效果
const isScrolled = ref(false)
const handleScroll = () => {
  isScrolled.value = window.scrollY > 10
}
onMounted(() => window.addEventListener('scroll', handleScroll))
onUnmounted(() => window.removeEventListener('scroll', handleScroll))

const form = reactive({
  bookTitle: '',
  editor: '',
  contractType: 'exclusive',
  email: '',
})

const errors = reactive({
  bookTitle: false,
  editor: false,
  email: '',
  file: false,
  fileSize: false,
  fileType: false,
  agreement: false,
})

const uploadedFiles = ref([])
const rawFile = ref(null)
const submitting = ref(false)
const fileInput = ref(null)
const agreed = ref(false)

const contractTypes = [
  { label: 'Exclusive', value: 'exclusive' },
  { label: 'Non-Exclusive', value: 'non-exclusive' },
  { label: 'Copyright Buyout', value: 'copyright-buyout' },
]

const goHome = () => { currentPage.value = 'home' }
const goWrite = () => { currentPage.value = 'write' }

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

const ALLOWED_EXTS = ['docx', 'txt']

const handleFileChange = (e) => {
  const files = Array.from(e.target.files)
  if (files.length) {
    const file = files[0]
    const ext = file.name.split('.').pop().toLowerCase()
    if (!ALLOWED_EXTS.includes(ext)) {
      errors.fileType = true
      errors.fileSize = false
      errors.file = false
      rawFile.value = null
      uploadedFiles.value = []
      nextTick(() => { if (fileInput.value) fileInput.value.value = '' })
      return
    }
    errors.fileType = false
    if (file.size > MAX_FILE_SIZE) {
      errors.fileSize = true
      errors.file = false
      rawFile.value = null
      uploadedFiles.value = []
      nextTick(() => { if (fileInput.value) fileInput.value.value = '' })
      return
    }
    errors.fileSize = false
    rawFile.value = file
    uploadedFiles.value = files.map(f => {
      const ext = f.name.split('.').pop().toLowerCase()
      return { name: f.name, ext }
    })
    errors.file = false
  }
}

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

const handleSubmit = async () => {
  errors.bookTitle = !form.bookTitle.trim()
  errors.editor = !form.editor.trim()
  if (!form.email.trim()) {
    errors.email = 'Please try again'
  } else if (!validateEmail(form.email)) {
    errors.email = 'Please enter a valid email'
  } else {
    errors.email = ''
  }
  errors.file = uploadedFiles.value.length === 0
  errors.agreement = !agreed.value

  if (errors.bookTitle || errors.editor || errors.email || errors.file || errors.agreement) return
  if (submitting.value) return
  submitting.value = true

  try {
    // 1. 上传文件
    const uploadRes = await uploadNovel(rawFile.value)
    const { url, fileName } = uploadRes.data || uploadRes

    // 2. 创建小说信息
    const contractTypeMap = { 'exclusive': 1, 'non-exclusive': 2, 'copyright-buyout': 3 }
    await createSubmission({
      bookTitle: form.bookTitle,
      editor: form.editor,
      contractType: contractTypeMap[form.contractType],
      email: form.email,
      url,
      fileName
    })

    currentPage.value = 'success'
  } catch (err) {
    console.error('提交失败', err)
  } finally {
    submitting.value = false
  }
}

const createNext = () => {
  form.bookTitle = ''
  form.editor = ''
  form.contractType = 'exclusive'
  form.email = ''
  rawFile.value = null
  uploadedFiles.value = []
  agreed.value = false
  Object.keys(errors).forEach(k => { errors[k] = false })
  currentPage.value = 'write'
}

// 输入时实时清除对应错误
watch(() => form.bookTitle, (val) => { if (val.trim()) errors.bookTitle = false })
watch(() => form.editor,    (val) => { if (val.trim()) errors.editor = false })
watch(() => form.email,     (val) => { if (val.trim()) errors.email = '' })
watch(agreed, (val) => { if (val) errors.agreement = false })
</script>

<style scoped lang="less">
// 主色
@primary: #5B4FE9;
@error-color: #E84040;
@text-main: #1a1a1a;
@text-gray: #999;
@bg-error: #FFF0F0;

.norem-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  color: @text-main;
  background: #fff;
  font-size: 16px;

  // ========== 导航 ==========
  .nav {
    display: flex;
    align-items: center;
    padding: 0 40px;
    height: 85px;
    background: transparent;
    border-bottom: 1px solid transparent;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 100;
    transition: background 0.3s, border-color 0.3s, box-shadow 0.3s;

    &.nav-scrolled {
      background: #fff;
      border-bottom-color: #f0f0f0;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    }

    .nav-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      margin: 0 auto;
      padding: 0 100px;

      .nav-logo {
        display: flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;

        .logo-icon {
          width: 44px;
          height: 44px;
          display: block;
          object-fit: contain;
        }

        .logo-text {
          font-size: 22px;
          font-weight: 700;
          color: @text-main;
        }
      }

      .nav-links {
        display: flex;
        gap: 48px;

        .nav-link {
          font-size: 17px;
          color: @text-gray;
          cursor: pointer;
          padding-bottom: 4px;
          border-bottom: 2px solid transparent;
          transition: color 0.2s;

          &.active {
            color: @text-main;
            font-weight: 700;
            border-bottom-color: @text-main;
          }

          &:hover:not(.active) {
            color: @text-main;
          }
        }
      }
    }
  }

  // ========== 首页 ==========
  .home-page {
    flex: 1;

    .home-img {
      width: 100%;
      display: block;
    }
  }

  // ========== Write 页 ==========
  .write-page {
    flex: 1;
  }

  // Hero
  .hero {
    width: 100%;
    overflow: hidden;

    .hero-img {
      width: 100%;
      display: block;
    }
  }

  // 表单区
  .form-section {
    padding: 80px 40px;
    width: 100%;

    .form-title {
      font-size: 60px;
      font-weight: 900;
      text-align: center;
      margin-bottom: 56px;
      color: @text-main;
    }
  }

  .form {
    max-width: 800px;
    width: 100%;
    margin: 0 auto;
  }

  .form-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 28px;
    gap: 20px;

    .form-label {
      width: 160px;
      flex-shrink: 0;
      text-align: right;
      font-size: 15px;
      color: @text-main;
      padding-top: 11px;
      line-height: 1.4;

      .required {
        color: @error-color;
        margin-right: 4px;
      }
    }

    .form-input-wrap {
      flex: 1;
    }

    .form-input {
      width: 88%;
      height: 46px;
      border: 1.5px solid #e5e5e5;
      border-radius: 16px;
      padding: 0 20px;
      font-size: 14px;
      color: @text-main;
      background: #fff;
      outline: none;
      transition: border-color 0.2s, background 0.2s;

      &::placeholder {
        color: #bbb;
      }

      &:focus {
        border-color: @primary;
      }

      &.error {
        background: @bg-error;
        border-color: transparent;
      }
    }

    .error-tip {
      color: @error-color;
      font-size: 12px;
      margin-top: 6px;
      padding-left: 20px;
    }
  }

  .form-item-upload {
    align-items: flex-start;
  }

  // Radio
  .form-radio-group {
    display: flex;
    align-items: center;
    gap: 28px;
    padding-top: 11px;

    .radio-label {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      font-size: 14px;
      color: @text-main;

      input[type="radio"] {
        display: none;
      }

      .radio-custom {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 2px solid #ccc;
        flex-shrink: 0;
        transition: all 0.2s;
        position: relative;

        &.checked {
          border-color: @primary;
          background-color: @primary;
          box-shadow: inset 0 0 0 3px #fff;
        }
      }
    }
  }

  // 上传
  .upload-area {
    flex: 1;
    padding-top: 4px;

    .file-list {
      display: flex;
      gap: 20px;
      margin-bottom: 16px;

      .file-item {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;

        .file-icon {
          width: 48px;
          height: 56px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;

          .file-ext {
            font-size: 11px;
            font-weight: 700;
            color: #fff;
          }

          &.docx { background: #4472C4; }
          &.txt  { background: #F4C842; }
        }

        .file-name {
          font-size: 12px;
          color: @text-gray;
        }
      }
    }

    .upload-btn {
      background: rgba(101, 85, 242, 0.08);
      color: rgba(101, 85, 242, 1);
      display: inline-flex;
      align-items: center;
      gap: 8px;
      height: 42px;
      padding: 0 28px;
      border-radius: 18px;
      font-size: 16px;
      cursor: pointer;
      transition: all 0.2s;
      &:hover {
        border-color: @primary;
        color: @primary;
      }

      &.error {
        background: @bg-error;
        border-color: transparent;
        color: @error-color;
      }

      .upload-text-error {
        color: @error-color;
      }

      .upload-icon {
        width: 22px;
        height: 22px;
        display: block;
      }
    }

    .upload-tip {
      margin-top: 8px;
      font-size: 12px;
      color: rgba(197, 197, 197, 1);

      &.error-tip {
        color: @error-color;
      }
    }
  }

  // 协议勾选
  .form-item-agreement {
    .agreement-label {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      font-size: 14px;
      color: @text-main;
      padding-top: 11px;

      .agreement-checkbox {
        display: none;
      }

      .checkbox-custom {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 2px solid #ccc;
        flex-shrink: 0;
        transition: all 0.2s;
        position: relative;

        &.checked {
          border-color: @primary;
          background-color: @primary;
          box-shadow: inset 0 0 0 3px #fff;
        }
      }

      .agreement-text {
        user-select: none;
      }

      .agreement-link {
        color: rgba(101, 85, 242, 1);
        text-decoration: none;
        cursor: pointer;
      }
    }
  }

  // 提交按钮
  .form-submit {
    display: flex;
    justify-content: center;
    margin-top: 48px;
  }

  .submit-btn {
    height: 50px;
    padding: 0 70px;
    border-radius: 16px;
    background: @primary;
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.1s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      opacity: 0.88;
    }

    &:active:not(:disabled) {
      opacity: 0.7;
      transform: scale(0.98);
    }

    .btn-loading {
      width: 16px;
      height: 16px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: #fff;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  // ========== 成功页 ==========
  .success-page {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 120px 40px;

    .success-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 28px;

      .success-icon {
        width: 88px;
        height: 88px;
      }

      .success-text {
        font-size: 16px;
        color: @text-main;
        text-align: center;
        line-height: 1.4;
      }
    }
  }

  // ========== Footer ==========
  .footer {
    background: #f5f5f5;
    padding: 40px;
    text-align: center;
    margin-top: auto;

    .footer-title {
      font-size: 16px;
      font-weight: 700;
      color: @text-main;
      margin-bottom: 12px;
    }

    .footer-links {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 16px;
      font-size: 14px;
      color: @text-main;
      margin-bottom: 10px;

      .footer-divider {
        color: #ddd;
      }

      a {
        color: @text-main;

        &:hover {
          color: @primary;
        }
      }
    }

    .footer-copy {
      font-size: 12px;
      color: @text-gray;
    }
  }

  // ========== 移动端适配 (≤768px) ==========
  @media (max-width: 768px) {
    // 导航
    .nav {
      height: 56px;
      padding: 0 16px;

      .nav-inner {
        padding: 0;

        .nav-logo {
          gap: 6px;
          .logo-icon { width: 26px; height: 26px; }
          .logo-text { font-size: 15px; }
        }

        .nav-links {
          gap: 24px;
          .nav-link { font-size: 14px; }
        }
      }
    }

    // 表单区
    .form-section {
      padding: 40px 20px;

      .form-title { font-size: 28px; margin-bottom: 32px; }
    }

    .form { max-width: 100%; }

    .form-item {
      flex-direction: column;
      gap: 8px;
      margin-bottom: 20px;

      .form-label {
        width: auto;
        text-align: left;
        padding-top: 0;
        font-size: 14px;
      }

      .form-input-wrap { width: 100%; }

      .form-input {
        width: 100%;
        height: 44px;
        font-size: 16px; // 防止 iOS 自动缩放
      }
    }

    // Radio 换行
    .form-radio-group {
      flex-wrap: wrap;
      gap: 14px;
      padding-top: 0;
    }

    // 上传区
    .upload-area {
      .upload-btn {
        height: 44px;
        font-size: 15px;
      }
    }

    // 提交按钮
    .form-submit { margin-top: 32px; }
    .submit-btn {
      width: 100%;
      padding: 0;
      font-size: 16px;
    }

    // 成功页
    .success-page {
      padding: 80px 24px;

      .success-content {
        .success-icon { width: 72px; height: 72px; }
        .success-text { font-size: 15px; }
      }
    }

    // Footer
    .footer {
      padding: 28px 20px;

      .footer-links {
        flex-direction: column;
        gap: 6px;

        .footer-divider { display: none; }
      }
    }
  }
}
</style>
 